// AIBulletText — the canonical renderer for AI analysis text across Hayah.
// Pairs with OUTPUT_BULLETS_BOLD in hayahPrompts.js. Splits the model's
// reply into one bullet per non-empty line, strips any leading bullet
// character the model adds anyway, then renders **double-asterisk** spans
// as marker-pen highlights (accent-tinted background + bold accent text)
// so the user can scan key numbers at a glance.
//
// Same visual treatment as ReadinessCard's rationale bullets — single
// source of truth so every AI analysis surface looks identical.

import React from "react";

const SPLIT_RE = /(\*\*[^*]+\*\*)/g;

export default function AIBulletText({
  text,
  accent,          // bullet + highlight colour (defaults to teal)
  textColor,       // bullet body colour
  fontSize = 13,
  lineHeight = 1.6,
  gap = 7,
}) {
  if (!text) return null;
  const raw = String(text).trim();
  if (!raw) return null;

  let bullets = raw.split(/\n+/).map(s => s.trim()).filter(Boolean);
  if (bullets.length === 1) {
    bullets = raw.split(/(?<=[.!?])\s+(?=[A-Z])/).map(s => s.trim()).filter(Boolean);
  }
  // Strip leading bullet glyphs / numbering but NEVER touch `**` (that's
  // markdown bold the renderer needs to keep). A single leading `*` is
  // ambiguous; safest is to leave `*` alone entirely — the OUTPUT_BULLETS_BOLD
  // prompt rule says the model must not emit bullet characters anyway.
  // Then strip a leading `**Label:**` pattern (e.g. "**Win:**", "**Gap:**")
  // that older prompts asked the model to emit — the prompt now forbids
  // labels, but cached responses and the occasional regression still ship
  // them. The bullet column already carries the visual prefix.
  bullets = bullets
    .map(b => b
      .replace(/^(?:[••‣◦⁃\-–—]+|\d+[.)])\s*/, "")
      .replace(/^\*\*\s*[A-Z][\w\s]{0,24}:\s*\*\*\s*/, "")
      .trim())
    .filter(Boolean);
  if (bullets.length === 0) return null;

  const acc = accent || "#4FD9C2";
  const ink = textColor || "rgba(244, 239, 224, 0.94)";

  const renderInline = (s) => {
    const parts = String(s || "").split(SPLIT_RE);
    return parts.map((p, i) => {
      if (p.startsWith("**") && p.endsWith("**")) {
        const inner = p.slice(2, -2);
        // Short spans (just a number / unit) stay on one line for a tighter
        // marker-pen look. Long spans (entire phrases the model wrapped in
        // bold) are allowed to wrap so they don't overflow the card.
        const short = inner.length <= 22 && !/\s.*\s/.test(inner);
        return (
          <strong key={i} style={{
            color: acc,
            fontWeight: 800,
            background: acc + "1f",
            padding: "0 5px",
            borderRadius: 4,
            ...(short ? { whiteSpace: "nowrap" } : {}),
          }}>{inner}</strong>
        );
      }
      return <span key={i}>{p}</span>;
    });
  };

  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap }}>
      {bullets.map((b, i) => (
        <li key={i} style={{
          display: "flex", gap: 9, alignItems: "flex-start",
          fontSize, color: ink, lineHeight,
        }}>
          <span style={{ color: acc, flexShrink: 0, fontWeight: 700, marginTop: 1 }}>•</span>
          <span style={{ flex: 1 }}>{renderInline(b)}</span>
        </li>
      ))}
    </ul>
  );
}
